import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, AlertCircle, Loader2, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { getFinancialHealth, type FinancialHealth } from "@/lib/financialLedger";

const TONE: Record<string, { dot: string; text: string; border: string; icon: JSX.Element }> = {
  green: {
    dot: "bg-primary", text: "text-primary", border: "border-primary/40",
    icon: <CheckCircle2 className="h-5 w-5 text-primary" />,
  },
  yellow: {
    dot: "bg-amber-500", text: "text-amber-600", border: "border-amber-400/50",
    icon: <AlertTriangle className="h-5 w-5 text-amber-600" />,
  },
  red: {
    dot: "bg-destructive", text: "text-destructive", border: "border-destructive/50",
    icon: <AlertCircle className="h-5 w-5 text-destructive" />,
  },
};

const fmtWhen = (iso: string | null) => {
  if (!iso) return "Never reconciled";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Never reconciled";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

/**
 * Live Financial Health. Reconciliation re-runs automatically whenever a
 * financial event has touched the books since the last successful run.
 */
export function FinancialHealthCard({ onChanged }: { onChanged?: () => void }) {
  const [health, setHealth] = useState<FinancialHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async (force = false) => {
    setLoading(true); setError(null);
    try {
      setHealth(await getFinancialHealth(force));
      onChanged?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(false); }, [load]);

  const tone = TONE[health?.indicator ?? "yellow"];

  return (
    <Card className={health ? tone.border : undefined}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            {loading && !health ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : tone.icon}
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Financial Health</p>
              <p className={`font-heading text-lg font-bold ${health ? tone.text : "text-foreground"}`}>
                {loading && !health ? "Checking the books…" : health?.label ?? "Unknown"}
              </p>
              <p className="text-xs text-muted-foreground">
                Last reconciled {fmtWhen(health?.last_reconciled_at ?? null)}
                {health?.reconciled_through ? ` · reconciled through ${health.reconciled_through}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => load(true)} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Re-check
            </Button>
            <Button size="sm" variant="outline" onClick={() => setExpanded((v) => !v)}>
              {expanded ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
              Details
            </Button>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {health && (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <Stat label="Unresolved issues" value={health.unresolved_count} />
            <Stat label="Duplicate transactions" value={health.counts.duplicate_transactions} />
            <Stat label="Missing ledger entries" value={health.counts.missing_ledger_entries} />
            <Stat label="Missing payroll links" value={health.counts.missing_payroll_links} />
            <Stat label="Missing payment links" value={health.counts.missing_payment_links} />
          </div>
        )}

        {expanded && health && (
          <div className="space-y-2 pt-1">
            <p className="text-xs text-muted-foreground">
              A period is only shown as Reconciled when every check below passes.
            </p>
            <ul className="space-y-1.5">
              {health.checks.map((c) => (
                <li key={c.key} className="flex items-start gap-2 text-sm">
                  {c.passed
                    ? <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    : <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">{c.label}</p>
                    <p className="text-xs text-muted-foreground">{c.detail}</p>
                  </div>
                </li>
              ))}
            </ul>

            {Object.entries(health.findings ?? {}).some(([, v]) => Array.isArray(v) && v.length > 0) && (
              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="text-sm font-medium">Open items</p>
                {Object.entries(health.findings).map(([key, list]) =>
                  Array.isArray(list) && list.length > 0 ? (
                    <div key={key} className="flex items-start justify-between gap-3 text-xs">
                      <span className="capitalize text-muted-foreground">{key.replace(/_/g, " ")}</span>
                      <Badge variant="destructive">{list.length}</Badge>
                    </div>
                  ) : null,
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border p-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-lg font-heading font-bold ${value > 0 ? "text-destructive" : "text-foreground"}`}>{value}</p>
    </div>
  );
}
